import { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Search, ArrowRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SeoHead } from "@/components/seo/SeoHead";

/**
 * Editorial-style 404. The site is presented as a newspaper, so the
 * miss page reads like a publication note rather than a stock error.
 */

const SUGGESTIONS = [
  {
    eyebrow: "Guide",
    title: "Comment delapresse choisit ses modèles IA",
    href: "/fonctionnalites#workflow",
  },
  {
    eyebrow: "Vitrine",
    title: "Cinq sites delapresse, en conditions réelles",
    href: "/vitrine",
  },
  {
    eyebrow: "FAQ",
    title: "Tout ce que les éditeurs demandent avant de lancer",
    href: "/ressources#faq",
  },
  {
    eyebrow: "Étude de cas",
    title: "Un quotidien régional, 180 articles par mois",
    href: "/ressources#cas",
  },
];

const NotFound = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [query, setQuery] = useState("");

  useEffect(() => {
    // Browsers can't easily set the HTTP status from SPA routing, but we
    // still want this surfaced in Sentry/log streams as a soft 404.
    console.error("404:", location.pathname);
  }, [location.pathname]);

  const suggestion = useMemo(() => {
    return SUGGESTIONS[Math.floor(Math.random() * SUGGESTIONS.length)];
  }, []);

  function onSearch(e: React.FormEvent) {
    e.preventDefault();
    const term = query.trim();
    if (!term) return;
    // No on-site article search yet; route to the resources page with a
    // fragment so future search wiring is non-breaking.
    navigate(`/ressources?q=${encodeURIComponent(term)}`);
  }

  return (
    <div className="min-h-screen bg-background">
      <SeoHead
        title={t("notFound.seoTitle")}
        description={t("notFound.seoDescription")}
        path={location.pathname}
        noindex
      />
      <div className="mx-auto max-w-3xl px-5 py-16 md:py-24">
        <p className="dateline mb-3">{t("notFound.dateline")}</p>
        <h1 className="font-serif text-4xl font-bold leading-tight text-foreground md:text-6xl">
          {t("notFound.heading")}
        </h1>
        <p className="mt-4 max-w-2xl font-serif text-xl italic leading-relaxed text-muted-foreground">
          {t("notFound.subhead")}
        </p>

        <hr className="my-10 border-border" />

        <form onSubmit={onSearch} className="mb-12">
          <label htmlFor="nf-search" className="dateline mb-2 block">
            {t("notFound.search.label")}
          </label>
          <div className="flex gap-2">
            <Input
              id="nf-search"
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={t("notFound.search.placeholder")}
              maxLength={120}
            />
            <Button type="submit" disabled={!query.trim()}>
              <Search className="h-4 w-4" aria-hidden />
              <span className="sr-only">{t("notFound.search.submit")}</span>
            </Button>
          </div>
        </form>

        <section className="rounded-lg border border-border bg-card p-6">
          <p className="dateline mb-3">{t("notFound.suggestion.heading")}</p>
          <p className="text-xs uppercase tracking-wider text-primary">{suggestion.eyebrow}</p>
          <h2 className="mt-2 font-serif text-2xl font-semibold text-foreground">
            <Link to={suggestion.href} className="hover:underline">
              {suggestion.title}
            </Link>
          </h2>
          <Link
            to={suggestion.href}
            className="mt-3 inline-flex items-center text-sm text-primary hover:underline"
          >
            {t("notFound.suggestion.cta")} <ArrowRight className="ml-1 h-3 w-3" aria-hidden />
          </Link>
        </section>

        <nav className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm">
          <Link to="/" className="font-medium text-foreground hover:underline">
            {t("notFound.nav.home")}
          </Link>
          <span aria-hidden className="text-muted-foreground">
            ·
          </span>
          <Link to="/ressources" className="text-muted-foreground hover:text-foreground">
            {t("notFound.nav.resources")}
          </Link>
          <span aria-hidden className="text-muted-foreground">
            ·
          </span>
          <Link to="/ressources#faq" className="text-muted-foreground hover:text-foreground">
            {t("notFound.nav.faq")}
          </Link>
          <span aria-hidden className="text-muted-foreground">
            ·
          </span>
          <Link to="/contact" className="text-muted-foreground hover:text-foreground">
            {t("notFound.nav.contact")}
          </Link>
        </nav>
      </div>
    </div>
  );
};

export default NotFound;
